import { BaseObjDao } from '@/dao';
import { GPIOReading } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import type { StationId } from '@/utilities/typealiases';
import { defineStore, storeToRefs } from 'pinia';
import { useEventStore } from './eventStore';
import { addToMapWithArray } from '@/utilities/arrayUtils';
import { ref, watch } from 'vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { computed } from 'vue';
import { compareDateTimes } from '@/utilities/dateutils';

interface GPIReadingSubscriptionOptions {
    triggerForExistingResults?: boolean;
}

type LoaderValueType = {
    orderedGPIOReadings: GPIOReading[];
    gpioReadings: Map<string, GPIOReading>;
    gpioStationMap: Map<StationId, GPIOReading[]>;
};

export const useGPIStore = defineStore('gpistore', () => {
    const eventStore = useEventStore();

    /**
     * List of all the active subscriptions for the shooting results
     */
    const gpiReadingsSubscriptions = new Set<(sr: GPIOReading[]) => void>();

    const loader = ref(
        new LoaderWithReset<LoaderValueType>({
            defaultValue: {
                gpioReadings: new Map(),
                gpioStationMap: new Map(),
                orderedGPIOReadings: [],
            },
            loaderFunction: async () => {
                const gpioReadings = await BaseObjDao.Load<GPIOReading>(GPIOReading, eventStore.eventID); // GPIOReadingsDao.GetGPIOReadingsFromStart(eventStore);

                const gpioReadingMap = new Map<string, GPIOReading>();
                const gpioStationMap = new Map<StationId, GPIOReading[]>();

                gpioReadings.forEach((gr) => {
                    gpioReadingMap.set(gr.Id!, gr);
                    addToMapWithArray(gpioStationMap, gr.StationID, gr);
                });

                const orderedGPIOReadings = gpioReadings.sort((a, b) => compareDateTimes(a.Time, b.Time));

                return {
                    gpioReadings: gpioReadingMap,
                    gpioStationMap: gpioStationMap,
                    orderedGPIOReadings: orderedGPIOReadings,
                };
            },
            waitOnBeforeLoad: eventStore.initpromise,
        }),
    );

    const reset = () => loader.value.Reset();

    const ensureLoaded = async () => await loader.value.Load();

    let signalrUnsubscribeFunction: Function | null = null;
    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        () => eventStore.eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            if (signalrUnsubscribeFunction != null) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.GPIOReading(
                {
                    EventID: eventStore.eventID,
                },
                async (readings) => await handleGpioReadings(readings),
            );
        },
        { immediate: true },
    );

    /**
     * Update and add the new GPIO readings to the store and trigger the subscriptions
     * @param readings
     */
    const handleGpioReadings = (readings: GPIOReading[]) => {
        readings.forEach((reading) => {
            if (reading.Id == null) {
                console.warn('Received a GPI reading notification with no ID, skipping', reading);
                return;
            }
            loader.value.Value.gpioReadings.set(reading.Id, reading);
            addToMapWithArray(loader.value.Value.gpioStationMap, reading.StationID, reading);

            // We need to add it to the ordered list as well. We do that by looking from the rear and add it after the first reading that is older than it. This is based on the assumption that new readings will usually be newer than the existing ones, so we start from the end of the list
            let addedToOrderedList = false;
            for (let i = loader.value.Value.orderedGPIOReadings.length - 1; i >= 0; i--) {
                if (loader.value.Value.orderedGPIOReadings[i].Time.getTime() <= reading.Time.getTime()) {
                    loader.value.Value.orderedGPIOReadings.splice(i + 1, 0, reading);
                    addedToOrderedList = true;

                    break;
                }
            }
            if (!addedToOrderedList) {
                loader.value.Value.orderedGPIOReadings.unshift(reading);
            }
        });

        gpiReadingsSubscriptions.forEach((cb) => cb(readings));
    };

    /**
     * Set up the subscription for GPIO readings. The callback will be triggered every time we get a new reading and if triggerForExistingResults is true, it will also be triggered with the existing readings
     * @param callback
     * @param options
     */
    const subscribeToGPIReadings = (callback: (sr: GPIOReading[]) => void, options?: GPIReadingSubscriptionOptions) => {
        gpiReadingsSubscriptions.add(callback);

        // If we should trigger for existing values, do so now
        if (options?.triggerForExistingResults) {
            ensureLoaded().then(() => {
                callback(Array.from(loader.value.Value.gpioReadings.values()));
            });
        }

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            gpiReadingsSubscriptions.delete(callback);
        };
    };

    /**
     * Get all the readings for a station.
     * @param  stationID
     * @param {Date | null} after
     * @returns {GPIOReading[]}
     */
    const getReadingsForStation = (stationID: StationId, after: Date | null = null) => {
        const allReadings = loader.value.Value.gpioStationMap.get(stationID) ?? [];
        if (after == null) {
            return allReadings;
        }
        return allReadings.filter((r) => r.Time > after);
    };

    return {
        /**
         * Ensure that all the data will be loaded from the server
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Subscribe to GPIO readings. The callback will be invoked every time we get a new GPIO reading
         */
        SubscribeToGPIReadings: subscribeToGPIReadings,

        /**
         * Get all the GPIO readings we have in the store
         * @returns
         */
        GetGPIReadings: () => Array.from(loader.value.Value.gpioReadings.values()),

        /**
         * Get all the readings for a station.
         * @param  stationID
         * @param {Date | null} after
         * @returns {GPIOReading[]}
         */
        GetGPIReadingsForStation: getReadingsForStation,

        /**
         * A list of all the GPIO readings ordered by time.
         * @returns {GPIOReading[]}
         */
        OrderedGPIOReadings: computed(() => loader.value.Value.orderedGPIOReadings),
    };
});

export const useGPIStoreRef = () => storeToRefs(useGPIStore());
